import { Phone, Mail, Instagram } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { Separator } from "@/components/ui/separator";
import { PHONE_HREF, PHONE_DISPLAY } from "@/lib/contact";

const legalLinks = [
  { label: "Aviso legal", to: "/aviso-legal" },
  { label: "Política de privacidad", to: "/politica-de-privacidad" },
];

export default function Footer() {
  const navigate = useNavigate();

  const handleNavClick = (e, href) => {
    e.preventDefault();
    const target = document.querySelector(href);
    if (target) {
      target.scrollIntoView({ behavior: "smooth" });
    } else {
      navigate("/" + href);
    }
  };

  return (
    <footer data-testid="footer" className="bg-[#1a1a1a] text-[#f8f6f3]">
      {/* Gold top line */}
      <div className="h-[2px] bg-gradient-to-r from-transparent via-[#c9a96e]/30 to-transparent" />
      
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 py-10">
        <div className="flex flex-col md:flex-row items-center justify-between gap-8">

          {/* Brand */}
          <div className="flex items-center gap-1 flex-shrink-0">
            <img src="/escudo.png" alt="Escudo A.F. Puertos" className="h-16 w-auto object-contain" />
            <img src="/logo-horizontal.png" alt="A.F. Puertos - Asesoría Fiscal" className="h-14 w-auto object-contain" />
          </div>

          {/* Contact */}
          <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-6">
            <a href={PHONE_HREF} data-testid="footer-phone" className="flex items-center gap-2 text-sm text-[#f8f6f3]/60 hover:text-[#c9a96e] transition-colors duration-300 font-light">
              <Phone className="w-4 h-4 text-[#c9a96e] flex-shrink-0" />
              {PHONE_DISPLAY}
            </a>
            <a href="mailto:info@afpuertos.com" data-testid="footer-email" className="flex items-center gap-2 text-sm text-[#f8f6f3]/60 hover:text-[#c9a96e] transition-colors duration-300 font-light">
              <Mail className="w-4 h-4 text-[#c9a96e] flex-shrink-0" />
              info@afpuertos.com
            </a>
            <a href="https://www.instagram.com/a.f.puertos?igsh=djVwbGVvZjhmdnVo" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm text-[#f8f6f3]/60 hover:text-[#c9a96e] transition-colors duration-300 font-light">
              <Instagram className="w-4 h-4 text-[#c9a96e] flex-shrink-0" />
              @a.f.puertos
            </a>
          </div>

          {/* Navigation */}
          <div className="flex items-center gap-5">
            {[
              { label: "Inicio", href: "#inicio" },
              { label: "Servicios", href: "#servicios" },
              { label: "Nosotros", href: "#nosotros" },
              { label: "Contacto", href: "#contacto" },
            ].map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => handleNavClick(e, link.href)}
                data-testid={`footer-nav-${link.label.toLowerCase()}`}
                className="text-sm text-[#f8f6f3]/60 hover:text-[#c9a96e] transition-colors duration-300 font-light"
              >
                {link.label}
              </a>
            ))}
          </div>

        </div>
      </div>

      {/* Brand description (SEO: name variants) */}
      <Separator className="bg-[#c9a96e]/10" />
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 py-5">
        <p className="text-xs text-center text-[#f8f6f3]/40 font-light leading-relaxed" style={{ fontFamily: "'Manrope', sans-serif" }}>
          A.F. Puertos —también conocida como AF Puertos o AFPuertos— es una asesoría fiscal,
          laboral, contable y jurídica en Alhaurín el Grande (Málaga).
        </p>
      </div>

      {/* Bottom bar */}
      <Separator className="bg-[#c9a96e]/10" />
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 py-6">
        <div className="flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-xs text-[#f8f6f3]/30 font-light" style={{ fontFamily: "'Manrope', sans-serif" }}>
            &copy; {new Date().getFullYear()} A.F. PUERTOS, S.L. Todos los derechos reservados.
          </p>
          <div className="flex items-center gap-6">
            {legalLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                data-testid={`legal-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="text-xs text-[#f8f6f3]/30 hover:text-[#c9a96e] transition-colors duration-300 font-light"
              >
                {link.label}
              </Link>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
}
